<template>
    <div :class="['pb-4 lg:pb-0', { 'lg:mb-8': isOpen }]">
        <button @click="isOpen = !isOpen" class="plp-filter" :aria-expanded="isOpen" aria-controls="price-filter-panel">
            <span>Prijs</span>
            <IconChevronDown
                :class="{ '!-rotate-180': isOpen }"
                class="size-3 -rotate-90 transition-all lg:-rotate-0" />
        </button>
        <div
            id="price-filter-panel"
            class="overflow-hidden pt-1.5 lg:duration-300"
            :class="{ 'filter-open': isOpen, 'filter-closed': !isOpen }"
            :aria-hidden="!isOpen"
            role="group">
            <div class="flex h-full flex-col">
                <div class="flex-1 overflow-y-auto">
                    <div class="mt-3 lg:hidden">
                        <button type="button" class="mb-8 flex items-center leading-none" @click="isOpen = false">
                            <ArrowLeftIcon class="mr-3 size-5 stroke-2" />
                            Terug
                        </button>
                        <span class="styled-as-h3 mb-5 block text-xl">Prijs</span>
                    </div>
                    <div class="flex flex-col gap-2 px-4">
                        <!-- Input fields -->
                        <div class="mb-1 flex items-center">
                            <div>
                                <label>
                                    <span class="mb-1 block">Van</span>
                                    <div class="flex items-center bg-green-200">
                                        <span class="currency-symbol pl-4 text-sm font-normal" aria-hidden="true"
                                            >€</span
                                        >
                                        <input
                                            ref="minInputRef"
                                            v-model="minPriceInput"
                                            type="text"
                                            inputmode="decimal"
                                            maxlength="5"
                                            :aria-label="`Minimum prijs in euro, tussen ${min} en ${max}`"
                                            @focusout="onMinPriceFocusOut"
                                            @focus="onMinPriceFocus($event)"
                                            class="input w-full bg-green-200 !pl-1 !text-sm font-normal" />
                                    </div>
                                </label>
                            </div>
                            <div>
                                <div
                                    class="mx-2 mt-3 w-[14px] rounded-full border-b-2 border-black"
                                    aria-hidden="true"></div>
                            </div>
                            <div>
                                <label>
                                    <span class="mb-1 block">Tot</span>
                                    <div class="flex items-center bg-green-200">
                                        <span class="currency-symbol pl-4 text-sm font-normal" aria-hidden="true"
                                            >€</span
                                        >
                                        <input
                                            ref="maxInputRef"
                                            v-model="maxPriceInput"
                                            type="text"
                                            inputmode="decimal"
                                            maxlength="5"
                                            :aria-label="`Maximum prijs in euro, tussen ${min} en ${max}`"
                                            @focusout="onMaxPriceFocusOut"
                                            @focus="onMaxPriceFocus($event)"
                                            class="input w-full bg-green-200 !pl-1 !text-sm font-normal" />
                                    </div>
                                </label>
                            </div>
                        </div>

                        <!-- Range Slider -->
                        <div
                            ref="sliderContainer"
                            class="relative mt-3 ml-1 h-8 w-full max-w-xl -translate-x-1 touch-none py-3 select-none">
                            <!-- Track background -->
                            <div
                                class="absolute top-1/2 right-3 left-3 z-10 h-1.5 -translate-y-1/2 rounded-md bg-gray-200">
                                <!-- Active track -->
                                <div
                                    class="absolute z-20 h-full rounded-md bg-green-600"
                                    :style="{
                                        left: `${leftHandlePercent}%`,
                                        right: `${100 - rightHandlePercent}%`,
                                    }"></div>
                            </div>

                            <!-- Left handle (minimum price) -->
                            <div
                                role="slider"
                                tabindex="0"
                                :aria-label="`Minimum price: ${formatPrice(minPrice)}`"
                                :aria-valuemin="min"
                                :aria-valuemax="max"
                                :aria-valuenow="minPrice"
                                class="absolute top-1/2 z-30 h-6 w-6 -translate-x-1/2 -translate-y-1/2 cursor-grab"
                                :style="{ left: `calc(${leftHandlePercent}% * 0.94 + 12px)` }"
                                @mousedown="startDrag($event, 'min')"
                                @touchstart.prevent="startDrag($event, 'min')">
                                <div class="h-full w-full rounded-full border-[2.4px] border-green-600 bg-white" />
                            </div>

                            <!-- Right handle (maximum price) -->
                            <div
                                role="slider"
                                tabindex="0"
                                :aria-label="`Maximum price: ${formatPrice(maxPrice)}`"
                                :aria-valuemin="min"
                                :aria-valuemax="max"
                                :aria-valuenow="maxPrice"
                                class="absolute top-1/2 z-30 h-6 w-6 -translate-x-1/2 -translate-y-1/2 cursor-grab"
                                :style="{ left: `calc(${rightHandlePercent}% * 0.94 + 12px)` }"
                                @mousedown="startDrag($event, 'max')"
                                @touchstart.prevent="startDrag($event, 'max')">
                                <div class="h-full w-full rounded-full border-[2.4px] border-green-600 bg-white" />
                            </div>
                        </div>
                    </div>
                </div>
                <div class="sticky right-0 bottom-0 left-0 bg-white pb-5 lg:hidden">
                    <button type="button" class="btn w-full bg-black text-white" @click="onViewClick">
                        {{ $snippets['headless.plp.view'] }} {{ total }} {{ $snippets['headless.plp.items'] }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
    import { ArrowLeftIcon } from '@heroicons/vue/24/outline';
    import { usePriceRangeFilter } from '~/composables/pages/usePriceRangeFilter';

    const props = withDefaults(
        defineProps<{
            label: string;
            index: number;
            min: number;
            max: number;
            total?: number;
        }>(),
        {
            min: 0,
            max: 1000,
        },
    );

    const isOpen = defineModel<boolean>('isOpen', { default: true });
    const mobileFiltersMenuStore = useMobileFiltersMenuStore();

    const {
        minPrice,
        maxPrice,
        minPriceInput,
        maxPriceInput,
        minInputRef,
        maxInputRef,
        leftHandlePercent,
        rightHandlePercent,
        sliderContainer,
        onMinPriceFocus,
        onMaxPriceFocus,
        onMinPriceFocusOut,
        onMaxPriceFocusOut,
        startDrag,
        formatPrice,
    } = usePriceRangeFilter({
        min: () => props.min,
        max: () => props.max,
    });

    /**
     * The "View N items" button blurs the active input by clicking. The blur
     * triggers the focusout handler, which already commits and schedules the
     * debounced URL push. We then close the panel + mobile menu.
     */
    const onViewClick = () => {
        isOpen.value = false;
        mobileFiltersMenuStore.closeMenu();
    };
</script>

<style scoped>
    .input {
        outline: none;
        border: none;
    }

    input[type='range']::-webkit-slider-thumb {
        pointer-events: all;
        width: 24px;
        height: 24px;
        -webkit-appearance: none;
    }
</style>
